/**
 * One board, full-bleed.
 *
 * The canvas is the page: a title bar, a way back, and the plane. Elements
 * arrive through the client's `sync`, which fires for every change whoever
 * made it, so this page holds no copy of its own beyond what it renders.
 *
 * **No guest token is read from this page's URL.** It used to take one from
 * `?token=`, which put a meeting credential in the address bar, in the history,
 * in the Worker's observability and — when the sign-in gate built its
 * `?next=` — in a request to the accounts service. Nothing ever produced such a
 * link: `/boards/:id` is inside the sign-in gate. A guest draws on the
 * meeting's board inside the meeting room, which hands the token to the socket's
 * own documented `?token=` (`relay-server/docs/calls-and-meetings.md`) — a
 * different URL, and the one place that credential belongs, because a browser
 * cannot set a header on an upgrade.
 */

import { useEffect, useState } from 'react';
import { ds } from '../ds';
import { Link } from '../router';
import { useT } from '../i18n';
import { getBoard, shareBoard } from '../lib/board/api';
import type { Board } from '../lib/board/types';
import type { PeerHere } from '../lib/board/peers';
import { Canvas } from '../components/board/Canvas';
import { useBoard } from '../components/board/useBoard';
import { ApiError } from '../lib/api';
import { useStore } from '../lib/relay/store';

export function BoardPage({ id }: { id: string }) {
  const t = useT();
  const [board, setBoard] = useState<Board | null>(null);
  const [loadProblem, setLoadProblem] = useState('');
  const [gone, setGone] = useState(false);
  const [moved, setMoved] = useState(0);

  // Read again whenever the board moves room while open: the person may have
  // just lost it, and a board they cannot reach is said so, not left trying.
  useEffect(() => {
    let live = true;
    getBoard(id)
      .then((b) => {
        if (live) setBoard(b);
      })
      .catch((err: unknown) => {
        if (!live || !(err instanceof ApiError)) return;
        if (err.status === 404 && moved > 0) setGone(true);
        else setLoadProblem(err.message);
      });
    return () => {
      live = false;
    };
  }, [id, moved]);

  if (gone) {
    return (
      <div style={{ padding: 'var(--space-24)', display: 'grid', gap: 'var(--space-12)', justifyItems: 'start' }}>
        <div>{t('boards.gone')}</div>
        <Link to="/boards" style={{ color: 'var(--text-link)', fontSize: 14 }}>
          {t('boards.back')}
        </Link>
      </div>
    );
  }
  return <BoardView id={id} board={board} loadProblem={loadProblem} onMoved={setMoved} onBoard={setBoard} />;
}

function BoardView({
  id,
  board,
  loadProblem,
  onMoved,
  onBoard
}: {
  id: string;
  board: Board | null;
  loadProblem: string;
  onMoved: (n: number) => void;
  onBoard: (b: Board) => void;
}) {
  const { Badge } = ds();
  const t = useT();
  const socket = useBoard(id);
  const { elements, status, others, apply, cursor } = socket;
  const problem = socket.problem || loadProblem;
  const me = useStore((s) => s.me());

  useEffect(() => {
    if (socket.moved) onMoved(socket.moved);
  }, [socket.moved, onMoved]);

  const title = board?.title || t('boards.untitled');
  const owner = !!board && !!me && board.ownerId === me.id && !board.meetingId;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 'var(--space-12)',
          padding: 'var(--space-12) var(--space-16)',
          borderBottom: '1px solid var(--border-subtle)'
        }}
      >
        <Link to="/boards" style={{ color: 'var(--text-link)', fontSize: 14 }}>
          {t('boards.back')}
        </Link>
        <div style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title}
        </div>
        {status !== 'open' && (
          <Badge tone={status === 'error' ? 'danger' : 'neutral'} dot>
            {status === 'error' ? problem || t('boards.trouble') : t('boards.connecting')}
          </Badge>
        )}
        <span style={{ flex: 1 }} />
        <Here people={others.here} />
        {owner && board ? <ShareControl board={board} onBoard={onBoard} /> : null}
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <Canvas
          elements={elements}
          apply={apply}
          name={title}
          others={others}
          onCursor={cursor}
          onLive={socket.live}
        />
      </div>
    </div>
  );
}

/** Who else has the board open: a coloured dot and a name each. */
function Here({ people }: { people: PeerHere[] }) {
  const t = useT();
  if (!people.length) return null;
  return (
    <div
      aria-label={t('boards.here')}
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-8)', fontSize: 13, color: 'var(--text-muted)', minWidth: 0 }}
    >
      {people.map((p) => (
        <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: p.colour }} />
          {p.label}
        </span>
      ))}
    </div>
  );
}

/** The owner's choice of who can open the board: themselves, or one of their
 *  conversations. Mauvely's own conversation is nobody's room to draw in. */
function ShareControl({ board, onBoard }: { board: Board; onBoard: (b: Board) => void }) {
  const { Select } = ds();
  const t = useT();
  const conversations = useStore((s) => s.conversations());
  const [problem, setProblem] = useState('');
  const options = [
    { value: '', label: t('boards.share.only') },
    ...conversations.filter((c) => !c.official).map((c) => ({ value: c.id, label: c.title }))
  ];
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <Select
        label={t('boards.share')}
        value={board.conversationId}
        options={options}
        onChange={(e) => {
          setProblem('');
          shareBoard(board.id, e.target.value || null)
            .then(onBoard)
            .catch((err: unknown) => setProblem(err instanceof ApiError ? err.message : t('boards.trouble')));
        }}
      />
      {problem ? <span style={{ fontSize: 12, color: 'var(--danger-600)' }}>{problem}</span> : null}
    </div>
  );
}
